import React from 'react';
import { Calendar } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';

const Booking = ({ t, bookingForm, setBookingForm, handleBookingSubmit, isSubmittingBooking }) => {
  return (
    <section id="booking" className="py-20 px-4 bg-white">
      <div className="container mx-auto max-w-4xl">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-navy-900 mb-4">{t.booking.title}</h2>
          <p className="text-lg text-gray-700">{t.booking.subtitle}</p>
        </div>

        <Card className="border-navy-100 shadow-lg">
          <CardContent className="pt-6">
            <form onSubmit={handleBookingSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="booking-name">{t.booking.form.fullName}{t.booking.form.required}</Label>
                  <Input
                    id="booking-name"
                    required
                    value={bookingForm.name}
                    onChange={(e) => setBookingForm({ ...bookingForm, name: e.target.value })}
                    className="border-gray-300 focus:border-navy-600"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="booking-email">{t.booking.form.email}{t.booking.form.required}</Label>
                  <Input
                    id="booking-email"
                    type="email"
                    required
                    value={bookingForm.email}
                    onChange={(e) => setBookingForm({ ...bookingForm, email: e.target.value })}
                    className="border-gray-300 focus:border-navy-600"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="booking-phone">{t.booking.form.phone}{t.booking.form.required}</Label>
                  <Input
                    id="booking-phone"
                    type="tel"
                    required
                    value={bookingForm.phone}
                    onChange={(e) => setBookingForm({ ...bookingForm, phone: e.target.value })}
                    className="border-gray-300 focus:border-navy-600"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="booking-company">{t.booking.form.company}</Label>
                  <Input
                    id="booking-company"
                    value={bookingForm.company}
                    onChange={(e) => setBookingForm({ ...bookingForm, company: e.target.value })}
                    className="border-gray-300 focus:border-navy-600"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="dispute-type">{t.booking.form.disputeType}{t.booking.form.required}</Label>
                  <select
                    id="dispute-type"
                    required
                    value={bookingForm.disputeType}
                    onChange={(e) => setBookingForm({ ...bookingForm, disputeType: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-gray-300 focus:border-navy-600 focus:outline-none focus:ring-2 focus:ring-navy-600 focus:ring-offset-0"
                  >
                    <option value="">{t.booking.form.selectType}</option>
                    <option value="shareholder">{t.booking.form.types.shareholder}</option>
                    <option value="client">{t.booking.form.types.client}</option>
                    <option value="supplier">{t.booking.form.types.supplier}</option>
                    <option value="social">{t.booking.form.types.social}</option>
                    <option value="training">{t.booking.form.types.training}</option>
                    <option value="other">{t.booking.form.types.other}</option>
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="preferred-day">{t.booking.form.preferredDay}{t.booking.form.required}</Label>
                  <select
                    id="preferred-day"
                    required
                    value={bookingForm.preferredDay}
                    onChange={(e) => setBookingForm({ ...bookingForm, preferredDay: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-gray-300 focus:border-navy-600 focus:outline-none focus:ring-2 focus:ring-navy-600 focus:ring-offset-0"
                  >
                    <option value="">{t.booking.form.selectDay}</option>
                    <option value="Monday">Monday</option>
                    <option value="Tuesday">Tuesday</option>
                    <option value="Wednesday">Wednesday</option>
                    <option value="Thursday">Thursday</option>
                    <option value="Friday">Friday</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="preferred-time">{t.booking.form.preferredTime}{t.booking.form.required}</Label>
                  <select
                    id="preferred-time"
                    required
                    value={bookingForm.preferredTime}
                    onChange={(e) => setBookingForm({ ...bookingForm, preferredTime: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-gray-300 focus:border-navy-600 focus:outline-none focus:ring-2 focus:ring-navy-600 focus:ring-offset-0"
                  >
                    <option value="">{t.booking.form.selectTime}</option>
                    <option value="09:00-12:00">09:00-12:00</option>
                    <option value="12:00-14:00">12:00-14:00</option>
                    <option value="14:00-17:00">14:00-17:00</option>
                    <option value="17:00-19:00">17:00-19:00</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="alternative-slots">{t.booking.form.alternativeSlots}</Label>
                <Input
                  id="alternative-slots"
                  value={bookingForm.alternativeSlots}
                  onChange={(e) => setBookingForm({ ...bookingForm, alternativeSlots: e.target.value })}
                  className="border-gray-300 focus:border-navy-600"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="booking-message">{t.booking.form.additionalInfo}</Label>
                <Textarea
                  id="booking-message"
                  rows={4}
                  value={bookingForm.message}
                  onChange={(e) => setBookingForm({ ...bookingForm, message: e.target.value })}
                  placeholder={t.booking.form.placeholder}
                  className="border-gray-300 focus:border-navy-600"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-navy-600 hover:bg-navy-700 text-lg py-6"
                disabled={isSubmittingBooking}
              >
                {isSubmittingBooking ? t.booking.form.submitting : (
                  <>
                    <Calendar className="mr-2 h-5 w-5" />
                    {t.booking.form.submit}
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default Booking;